"use client";

import { create } from "zustand";
import { persist } from "zustand/middleware";

export const ENERGY_MAX = 30;
/** Spent when entering the kitchen from matchmaking (online only). */
export const ENERGY_COST_PER_MATCH = 5;
const REGEN_MS_PER_POINT = 3 * 60 * 1000;

function clamp(n: number, max: number): number {
  return Math.max(0, Math.min(max, Math.round(n)));
}

type EnergyState = {
  energy: number;
  energyMax: number;
  updatedAt: number;
  /** Advance regen from timestamps (call on read paths). */
  tickRegen: () => void;
  trySpendForMatch: (cost?: number) => boolean;
  refundLastMatch: (cost?: number) => void;
  addEnergy: (n: number) => void;
};

export const useEnergyMeta = create<EnergyState>()(
  persist(
    (set, get) => ({
      energy: ENERGY_MAX,
      energyMax: ENERGY_MAX,
      updatedAt: Date.now(),

      tickRegen() {
        const now = Date.now();
        let { energy, energyMax, updatedAt } = get();
        if (energy >= energyMax) {
          if (updatedAt !== now) set({ updatedAt: now });
          return;
        }
        const elapsed = now - updatedAt;
        const gained = Math.floor(elapsed / REGEN_MS_PER_POINT);
        if (gained <= 0) return;
        const next = Math.min(energyMax, energy + gained);
        const usedMs = gained * REGEN_MS_PER_POINT;
        let nextUpdated = updatedAt + usedMs;
        if (next >= energyMax) nextUpdated = now;
        set({ energy: next, updatedAt: nextUpdated });
      },

      trySpendForMatch(cost = ENERGY_COST_PER_MATCH) {
        get().tickRegen();
        const { energy, energyMax } = get();
        if (energy < cost) return false;
        set({ energy: clamp(energy - cost, energyMax), updatedAt: Date.now() });
        return true;
      },

      refundLastMatch(cost = ENERGY_COST_PER_MATCH) {
        const { energy, energyMax } = get();
        set({
          energy: clamp(energy + cost, energyMax),
          updatedAt: Date.now(),
        });
      },

      addEnergy(n) {
        get().tickRegen();
        const { energy, energyMax } = get();
        set({ energy: clamp(energy + n, energyMax), updatedAt: Date.now() });
      },
    }),
    { name: "cwf-energy-meta" },
  ),
);

export function tickEnergyRegen(): void {
  useEnergyMeta.getState().tickRegen();
}
